import moment from 'moment-timezone';
import { MAX_MONTHS_BACK } from './ChangeEvents.consts';

/**
 * Quick ranges for the date pickers in this module.
 *
 * The API refuses anything more than three months back, so nothing longer is
 * offered here. Built as a function so the dates are correct at the moment a
 * screen opens, not at the moment the bundle loaded. Call it from `data()`;
 * never store the result in a module constant.
 *
 * `timezone` is the account time zone. The picker hands the chosen dates back
 * as strings that the screens read in that zone, so "Today" must be today in
 * that zone, not in the browser's.
 * @param {string} [timezone] an IANA zone such as `Asia/Singapore`
 * @returns {Record<string, import('moment').Moment[]>}
 */
export function buildRanges(timezone) {
  const now = () => (timezone ? moment.tz(timezone) : moment());
  return {
    Today: [now().startOf('day'), now().endOf('day')],
    Yesterday: [now().subtract(1, 'days').startOf('day'), now().subtract(1, 'days').endOf('day')],
    'Last 7 Days': [now().subtract(6, 'days').startOf('day'), now().endOf('day')],
    'Last 30 Days': [now().subtract(29, 'days').startOf('day'), now().endOf('day')],
    'This Month': [now().startOf('month'), now().endOf('month')],
    'Last Month': [now().subtract(1, 'month').startOf('month'), now().subtract(1, 'month').endOf('month')],
    'Last 3 Months': [now().subtract(MAX_MONTHS_BACK, 'month').startOf('day'), now().endOf('day')]
  };
}

/**
 * The earliest day a picker may offer, in the account time zone. It is the
 * same day the "Last 3 Months" quick range starts on.
 * @param {string} [timezone]
 * @returns {import('moment').Moment}
 */
export function earliestPickableDay(timezone) {
  const now = timezone ? moment.tz(timezone) : moment();
  return now.subtract(MAX_MONTHS_BACK, 'month').startOf('day');
}
